import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormLabel,
    Input,
    Select,
    Textarea,
    Text,
    VStack,
    HStack,
    Flex,
    IconButton,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Alert,
    AlertIcon,
    AlertDescription,
    Grid,
    Switch,
    Radio,
    RadioGroup,
    Stack,
    Tag,
    Image,
    Spinner,
    Icon,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Divider,
} from '@chakra-ui/react';

import { EmailPreview, InAppPreview, PushPreview, SMSPreview, VoicePreview, WhatsAppPreview } from './ChannelPreviewBox';
import {
    LuInfo,
    LuMessageCircle,
    LuMessageSquare,
    LuMail,
    LuBell,
    LuPhone,
    LuSmartphone,
    LuArrowRight,
    LuArrowLeft,
    LuPlus,
    LuHash,
    LuTarget,
    LuTrash2,
    LuCheckCircle,
    LuUpload,
    LuX,
    LuImage,
} from 'react-icons/lu';
import { MESSAGE_EXPIRY_OPTIONS } from '../../../utils/DefaultFunction';



const validateSignatureHash = (hash) => {
    const regex = /^[a-zA-Z0-9]{11}$/;
    return regex.test(hash) && hash.length === 11;
};

const containsEmojis = (text) => {
    const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
    return emojiRegex.test(text);
};

const containsVariables = (text) => {
    return /\{\{[^{}]*\}\}/.test(text);
};

// Image Upload Component with better UI
const ImageUploader = ({ label, accept, onChange, imageUrl, onRemove, uploading, targetId, hint, error }) => {
    const fileInputRef = React.useRef(null);

    const getFileTypeIcon = () => {
        if (accept === 'image/*') return <LuImage size={48} />;
        if (accept === 'video/*') return <LuImage size={48} />;
        return <LuUpload size={48} />;
    };

    const getAcceptedFormats = () => {
        if (accept === 'image/*') return 'JPEG, PNG (Max 5MB)';
        if (accept === 'video/*') return 'MP4, 3GPP (Max 16MB)';
        return 'PDF, DOCX, PPTX, XLSX (Max 100MB)';
    };

    return (
        <FormControl>
            <FormLabel fontWeight="semibold">{label}</FormLabel>

            {imageUrl ? (
                <VStack spacing={3} align="stretch">
                    <Box
                        position="relative"
                        borderWidth="2px"
                        borderRadius="lg"
                        borderColor="green.400"
                        overflow="hidden"
                        bg="white"
                        p={2}
                    >
                        {accept === 'image/*' ? (
                            <Image
                                src={imageUrl}
                                alt="Uploaded preview"
                                maxH="200px"
                                w="full"
                                objectFit="contain"
                                borderRadius="md"
                            />
                        ) : (
                            <Box textAlign="center" py={4}>
                                <LuCheckCircle size={48} color="green" style={{ margin: '0 auto' }} />
                                <Text fontSize="sm" fontWeight="medium" mt={2} color="green.600">
                                    File Uploaded Successfully
                                </Text>
                            </Box>
                        )}
                        <IconButton
                            aria-label="Remove file"
                            icon={<LuX />}
                            size="sm"
                            bg="white"
                            color="black"
                            position="absolute"
                            top={3}
                            right={3}
                            borderRadius="full"
                            boxShadow="md"
                            onClick={onRemove}
                            _hover={{ bg: "none" }}
                        />
                    </Box>

                    <Box bg="gray.50" p={3} borderRadius="md" borderWidth="1px">
                        <HStack spacing={2}>
                            <LuCheckCircle color="green" />
                            <Text fontSize="xs" fontWeight="medium" color="gray.700" noOfLines={1} flex={1}>
                                {imageUrl}
                            </Text>
                        </HStack>
                    </Box>

                    {hint && (
                        <Text fontSize="xs" color="gray.600">
                            {hint}
                        </Text>
                    )}

                    {error && (
                        <Alert status="error" fontSize="sm">
                            <AlertIcon />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                </VStack>
            ) : (
                <VStack spacing={3} align="stretch">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={accept}
                        onChange={onChange}
                        style={{ display: 'none' }}
                        id={targetId}
                        disabled={uploading}
                    />

                    <Box
                        as="label"
                        htmlFor={targetId}
                        cursor={uploading ? 'not-allowed' : 'pointer'}
                        borderWidth="2px"
                        borderStyle="dashed"
                        borderColor={uploading ? "gray.300" : "gray.200"}
                        borderRadius="lg"
                        p={8}
                        textAlign="center"
                        bg={uploading ? "gray.50" : "white"}
                        transition="all 0.2s"
                        _hover={!uploading ? {
                            borderColor: "blue.400",
                            bg: "blue.50"
                        } : {}}
                    >
                        <VStack spacing={3}>
                            {uploading ? (
                                <>
                                    <Spinner size="xl" color="blue.500" thickness="3px" />
                                    <Text fontWeight="semibold" color="blue.600">
                                        Uploading...
                                    </Text>
                                    <Text fontSize="xs" color="gray.600">
                                        Please wait while we upload your file
                                    </Text>
                                </>
                            ) : (
                                <>
                                    <Box color="gray.400">
                                        {getFileTypeIcon()}
                                    </Box>
                                    <VStack spacing={1}>
                                        <Text fontWeight="semibold" color="gray.700">
                                            Click to upload or drag and drop
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {getAcceptedFormats()}
                                        </Text>
                                    </VStack>
                                    <Button
                                        size="sm"
                                        colorScheme="blue"
                                        variant="solid"
                                        leftIcon={<LuUpload />}
                                        pointerEvents="none"
                                    >
                                        Choose File
                                    </Button>
                                </>
                            )}
                        </VStack>
                    </Box>

                    {hint && (
                        <Text fontSize="xs" color="gray.600">
                            {hint}
                        </Text>
                    )}

                    {error && (
                        <Alert status="error" fontSize="sm">
                            <AlertIcon />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                </VStack>
            )}
        </FormControl>
    );
};



export function ChannelTabsFixed(props) {
    const cardBg = 'white';
    const borderColor = 'gray.200';
    const bgMuted = 'gray.50';



    const hanldeOtpTypeChange = (value) => {
        props.setOtpType(value);
    };

    const getExpiryOptionsForTemplate = (templateType) => {
        return MESSAGE_EXPIRY_OPTIONS[templateType] || MESSAGE_EXPIRY_OPTIONS['1'];
    };

    const expiryOptions = getExpiryOptionsForTemplate(props.templateType);
    const isDocumentSelected = props.whatsappHeader.type === 'document'
    const channelPanels = {
        'Whatsapp': () => (
            <VStack spacing={4} align="stretch">
                {/* Enable/Disable Toggle */}
                <Flex
                    p={4}
                    bg={bgMuted}
                    borderRadius="lg"
                    justify="space-between"
                    align="center"
                >
                    <HStack spacing={3}>
                        <LuMessageCircle size={20} color="green" />
                        <Box>
                            <Text fontWeight="medium">WhatsApp Channel</Text>
                            <Text fontSize="sm" color="gray.600">
                                Rich messaging with media and buttons
                            </Text>
                        </Box>
                    </HStack>

                    <HStack spacing={2}>
                        <Switch
                            id="enable-whatsapp"
                            isChecked={props.selectedChannels.includes('Whatsapp')}
                            onChange={() => props.handleChannelToggle('Whatsapp')}
                            colorScheme="green"
                        />
                        <FormLabel
                            htmlFor="enable-whatsapp"
                            mb={0}
                            cursor="pointer"
                            fontWeight="medium"
                        >
                            {props.selectedChannels.includes('Whatsapp') ? 'Enabled' : 'Disabled'}
                        </FormLabel>
                    </HStack>
                </Flex>

                {props.selectedChannels.includes('Whatsapp') && (
                    <>
                        {/* Sync Button */}
                        {props.selectedChannels.includes('In-App Notification') && (
                            <Flex justify="flex-end">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    leftIcon={<LuArrowRight />}
                                    onClick={props.syncWhatsAppToInApp}
                                >
                                    Sync to In-App
                                </Button>
                            </Flex>
                        )}

                        {/* Settings and Preview Grid */}
                        <Grid templateColumns={{ base: '1fr', lg: '3fr 1fr' }} gap={6}>
                            {/* Left Panel - Settings */}
                            <Box>
                                <Box className='box-style-card'>
                                    <Box>
                                        {/* {props.isTemplateApproved && (
                                            <Alert bg={"red.50"} border={"1px"} borderStyle={"solid"} borderColor={"red.200"} borderRadius={"8.75px"} p={4} my={1}>
                                                <LuInfo color='#e7000b' style={{ marginRight: "1rem" }} />
                                                Your WhatsApp template has been approved. Whatsapp message content can no longer be modified.
                                            </Alert>
                                        )} */}
                                        <VStack spacing={6} align="stretch">
                                            {/* Header Section */}
                                            <Box>
                                                <VStack spacing={4} align="stretch">
                                                    {/* <Box display={`${isDocumentSelected ? "flex" : "block"}`}> */}
                                                    <Text fontWeight="medium" >Header (Optional)</Text>
                                                        {props.templateType !== '1' && (
                                                            <Box display={`${isDocumentSelected ? "flex" : "block"}`} gap={4}>
                                                                
                                                                <FormControl>
                                                                    <FormLabel>Header Type</FormLabel>
                                                                    <Select
                                                                        // disabled={props.isTemplateApproved}
                                                                        value={props.whatsappHeader.type || 'none'}
                                                                        onChange={(e) => props.handleWhatsAppHeaderTypeChange(e.target.value === 'none' ? null : e.target.value)}
                                                                    >
                                                                        <option value="none">None</option>
                                                                        <option value="text">Text</option>
                                                                        <option value="image">Image</option>
                                                                        <option value="video">Video</option>
                                                                        <option value="document">Document</option>
                                                                        <option value="location">Location</option>
                                                                    </Select>
                                                                </FormControl>
                                                                {props.whatsappHeader.type === 'document' && (
                                                                    <FormControl isRequired>
                                                                        <FormLabel>Document Filename</FormLabel>
                                                                        <Input
                                                                            value={props.whatsappHeader.document_filename || ''}
                                                                            onChange={(e) => props.setWhatsappHeader(prev => ({ ...prev, document_filename: e.target.value }))}
                                                                            placeholder="Enter document filename (e.g., invoice.pdf)"
                                                                            maxLength={100}
                                                                            // disabled={props.isTemplateApproved}
                                                                        />
                                                                        <Flex justify="flex-end" mt={1}>
                                                                            {props.getCharacterCount(props.whatsappHeader.document_filename || '', 100)}
                                                                        </Flex>
                                                                        <Text fontSize="xs" color="gray.600" >
                                                                            This filename will be displayed to recipients
                                                                        </Text>
                                                                    </FormControl>
                                                                )}
                                                            </Box>
                                                        )}



                                                    {/* </Box> */}
                                                    {(props.whatsappHeader.type === 'text' && props.templateType !== '1') && (
                                                        <FormControl>
                                                            <FormLabel isRequired>Header Text</FormLabel>
                                                            <Input
                                                                value={props.whatsappHeader.content}
                                                                onChange={(e) => props.setWhatsappHeader(prev => ({ ...prev, content: e.target.value }))}
                                                                placeholder="Enter header text"
                                                                maxLength={60}
                                                                // disabled={props.isTemplateApproved}
                                                            />
                                                            <Flex justify="flex-end" mt={1}>
                                                                {props.getCharacterCount(props.whatsappHeader.content, 60)}
                                                            </Flex>

                                                            {containsVariables(props.whatsappHeader.content) && (
                                                                <Alert status="error" mt={2}>
                                                                    <AlertIcon />
                                                                    <AlertDescription fontSize="sm">
                                                                        Variables are not allowed in header
                                                                    </AlertDescription>
                                                                </Alert>
                                                            )}
                                                        </FormControl>
                                                    )}



                                                    {(props.whatsappHeader.type === 'image' ||
                                                        props.whatsappHeader.type === 'video' ||
                                                        props.whatsappHeader.type === 'document') && (
                                                            <ImageUploader
                                                                label={`${props.whatsappHeader.type.charAt(0).toUpperCase() + props.whatsappHeader.type.slice(1)} Header`}
                                                                accept={
                                                                    props.whatsappHeader.type === 'image' ? 'image/*' :
                                                                        props.whatsappHeader.type === 'video' ? 'video/*' :
                                                                            'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                                                                }
                                                                onChange={(e) => props.handleFileUpload(e, 'whatsapp-header')}
                                                                imageUrl={props.whatsappHeader.file}
                                                                onRemove={() => props.removeImage('whatsapp-header')}
                                                                uploading={props.uploading}
                                                                targetId="whatsapp-header-file"
                                                                hint="This will appear at the top of your WhatsApp message"
                                                            />
                                                        )}

                                                    {props.whatsappHeader.type === 'location' && (
                                                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                                            <FormControl isRequired>
                                                                <FormLabel>Location Name</FormLabel>
                                                                <Input
                                                                    placeholder="Enter location name"
                                                                    // disabled={props.isTemplateApproved}
                                                                    value={props.whatsappHeader.location_name || ''}
                                                                    onChange={(e) => props.setWhatsappHeader({
                                                                        ...props.whatsappHeader,
                                                                        location_name: e.target.value
                                                                    })}
                                                                />
                                                            </FormControl>
                                                            <FormControl isRequired>
                                                                <FormLabel>Address</FormLabel>
                                                                <Input
                                                                    placeholder="Enter address"
                                                                    // disabled={props.isTemplateApproved}
                                                                    value={props.whatsappHeader.location_address || ''}
                                                                    onChange={(e) => props.setWhatsappHeader({
                                                                        ...props.whatsappHeader,
                                                                        location_address: e.target.value
                                                                    })}
                                                                />
                                                            </FormControl>
                                                            <FormControl isRequired>
                                                                <FormLabel>Latitude</FormLabel>
                                                                <Input
                                                                    placeholder="Enter latitude"
                                                                    // disabled={props.isTemplateApproved}
                                                                    type="number"
                                                                    step="any"
                                                                    value={props.whatsappHeader.latitude || ''}
                                                                    onChange={(e) => props.setWhatsappHeader({
                                                                        ...props.whatsappHeader,
                                                                        latitude: e.target.value
                                                                    })}
                                                                />
                                                            </FormControl>
                                                            <FormControl isRequired>
                                                                <FormLabel>Longitude</FormLabel>
                                                                <Input
                                                                    placeholder="Enter longitude"
                                                                    // disabled={props.isTemplateApproved}
                                                                    type="number"
                                                                    step="any"
                                                                    value={props.whatsappHeader.longitude || ''}
                                                                    onChange={(e) => props.setWhatsappHeader({
                                                                        ...props.whatsappHeader,
                                                                        longitude: e.target.value
                                                                    })}
                                                                />
                                                            </FormControl>
                                                        </Grid>
                                                    )}
                                                </VStack>
                                            </Box>

                                            {/* Body Section */}
                                            <FormControl isRequired>
                                                <FormLabel>Body</FormLabel>
                                                <Textarea
                                                    value={props.whatsappBody}
                                                    onChange={(e) => props.setWhatsappBody(e.target.value)}
                                                    placeholder="Enter message body"
                                                    rows={4}
                                                    maxLength={1024}
                                                    isReadOnly={props.templateType === '1' && props.authAddSecurityRecommendation}
                                                    bg={(props.templateType === '1' && props.authAddSecurityRecommendation) ? bgMuted : 'white'}
                                                    cursor={(props.templateType === '1' && props.authAddSecurityRecommendation) ? 'not-allowed' : 'text'}
                                                    // disabled={props.isTemplateApproved}
                                                />
                                                <Flex justify="flex-end" mt={1}>
                                                    {props.getCharacterCount(props.whatsappBody, 1024)}
                                                </Flex>
                                                {containsEmojis(props.whatsappBody) && (
                                                    <Alert status="error" mt={2}>
                                                        <AlertIcon />
                                                        <AlertDescription fontSize="sm">
                                                            Emojis are not allowed in message body
                                                        </AlertDescription>
                                                    </Alert>
                                                )}
                                            </FormControl>

                                            {/* Footer Section */}
                                            {props.templateType !== '1' && (
                                                <FormControl>
                                                    <FormLabel>Footer (Optional)</FormLabel>
                                                    <Input
                                                        value={props.whatsappFooter}
                                                        onChange={(e) => props.setWhatsappFooter(e.target.value)}
                                                        placeholder="Enter footer text"
                                                        maxLength={60}
                                                        // disabled={props.isTemplateApproved}
                                                    />
                                                    <Flex justify="flex-end" mt={1}>
                                                        {props.getCharacterCount(props.whatsappFooter, 60)}
                                                    </Flex>
                                                    {containsVariables(props.whatsappFooter) && (
                                                        <Alert status="error" mt={2}>
                                                            <AlertIcon />
                                                            <AlertDescription fontSize="sm">
                                                                Variables are not allowed in footer
                                                            </AlertDescription>
                                                        </Alert>
                                                    )}
                                                </FormControl>
                                            )}

                                            {/* Buttons or OTP Configuration */}
                                            {props.templateType === '1' ? (
                                                <VStack spacing={4} align="stretch">
                                                    <FormControl>
                                                        <FormLabel>OTP Authentication Type</FormLabel>
                                                        <Text fontSize="xs" color="gray.600" mb={2}>
                                                            Choose how customers send the code from WhatsApp to your app.
                                                        </Text>
                                                        <Select
                                                            value={props.otpType}
                                                            onChange={(e) => hanldeOtpTypeChange(e.target.value)}
                                                            placeholder="Select authentication type"
                                                            // disabled={props.isTemplateApproved}
                                                        >
                                                            <option value="COPY_CODE">Copy Code</option>
                                                            <option value="ONE_TAP">One-tap Autofill</option>
                                                            <option value="ZERO_TAP">Zero-tap Autofill</option>
                                                        </Select>
                                                    </FormControl>

                                                    {(props.otpType === 'ONE_TAP' || props.otpType === 'ZERO_TAP') && (
                                                        <Box p={4} borderWidth="1px" borderRadius="md" bg={bgMuted}>
                                                            <VStack spacing={4} align="stretch">
                                                                <Box>
                                                                    <Text fontWeight="medium" mb={2}>App Setup</Text>
                                                                    <Text fontSize="sm" color="gray.600">You can add up to 5 apps.</Text>
                                                                </Box>

                                                                {props.otpApps.map((app, index) => (
                                                                    <Box key={index} borderTopWidth={index > 0 ? '1px' : 0} pt={4}>
                                                                        <Flex justify="space-between" mb={3}>
                                                                            <Text fontSize="sm" fontWeight="medium">App {index + 1}</Text>
                                                                            {props.otpApps.length > 1 && (
                                                                                <IconButton
                                                                                    aria-label="Remove app"
                                                                                    icon={<LuTrash2 />}
                                                                                    size="sm"
                                                                                    colorScheme="red"
                                                                                    variant="ghost"
                                                                                    onClick={() => props.removeOtpApp(index)}
                                                                                />
                                                                            )}
                                                                        </Flex>

                                                                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                                                            <FormControl isRequired>
                                                                                <FormLabel fontSize="sm">Package Name</FormLabel>
                                                                                <Input
                                                                                    value={app.package_name}
                                                                                    onChange={(e) => props.updateOtpApp(index, 'package_name', e.target.value)}
                                                                                    placeholder="com.example.app"
                                                                                    maxLength={224}
                                                                                    size="sm"
                                                                                    // disabled={props.isTemplateApproved}
                                                                                />
                                                                                <Flex justify="flex-end" mt={1}>
                                                                                    <Text fontSize="xs" color="gray.600">
                                                                                        {app.package_name.length}/224
                                                                                    </Text>
                                                                                </Flex>
                                                                            </FormControl>

                                                                            <FormControl isRequired>
                                                                                <FormLabel fontSize="sm">App Signature Hash</FormLabel>
                                                                                <Input
                                                                                    value={app.signature_hash}
                                                                                    onChange={(e) => props.updateOtpApp(index, 'signature_hash', e.target.value)}
                                                                                    placeholder="xXxXxXxXxXx"
                                                                                    maxLength={11}
                                                                                    size="sm"
                                                                                    isInvalid={app.signature_hash && !validateSignatureHash(app.signature_hash)}
                                                                                    // disabled={props.isTemplateApproved}
                                                                                />
                                                                                <Flex justify="flex-end" mt={1}>
                                                                                    <Text fontSize="xs" color={app.signature_hash && !validateSignatureHash(app.signature_hash) ? "red.500" : "gray.600"}>
                                                                                        {app.signature_hash.length}/11
                                                                                    </Text>
                                                                                </Flex>
                                                                                {app.signature_hash && !validateSignatureHash(app.signature_hash) && (
                                                                                    <Text fontSize="xs" color="red.500" mt={1}>
                                                                                        Must be exactly 11 alphanumeric characters (a-z, A-Z, 0-9)
                                                                                    </Text>
                                                                                )}
                                                                            </FormControl>
                                                                        </Grid>
                                                                    </Box>
                                                                ))}

                                                                {props.otpApps.length < 5 && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        leftIcon={<LuPlus />}
                                                                        onClick={props.addOtpApp}
                                                                        w="full"
                                                                    >
                                                                        Add another app
                                                                    </Button>
                                                                )}
                                                            </VStack>
                                                        </Box>
                                                    )}
                                                </VStack>
                                            ) : (
                                                <Box>
                                                    <Flex justify="space-between" align="center" mb={3}>
                                                        <Text fontWeight="medium">Call-to-Action Buttons (Optional)</Text>
                                                        {props.whatsappButtons.length < 3 && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                leftIcon={<LuPlus />}
                                                                onClick={props.addWhatsAppButton}
                                                                // disabled={props.isTemplateApproved}
                                                            >
                                                                Add Button
                                                            </Button>
                                                        )}
                                                    </Flex>
                                                    {props.renderButtonConfiguration(
                                                        props.whatsappButtons,
                                                        props.updateWhatsAppButton,
                                                        props.removeWhatsAppButton,
                                                        3,
                                                        'Button'
                                                    )}
                                                </Box>
                                            )}

                                            {props.templateType === '1' && (
                                                <Box p={4} borderWidth="1px" borderRadius="md" bg={bgMuted}>
                                                    <VStack spacing={4} align="stretch">
                                                        <Box>
                                                            <Text fontWeight="medium" mb={2}>Content Options</Text>
                                                            <Text fontSize="sm" color="gray.600" mb={3}>
                                                                Content for authentication message templates can't be edited. You can add additional content from the options below.
                                                            </Text>
                                                        </Box>

                                                        <Checkbox
                                                            isChecked={props.authAddSecurityRecommendation}
                                                            onChange={(e) => props.setAuthAddSecurityRecommendation(e.target.checked)}
                                                            // disabled={props.isTemplateApproved}
                                                        >
                                                            Add security recommendation
                                                        </Checkbox>

                                                        <Checkbox
                                                            isChecked={props.authAddExpirationTime}
                                                            onChange={(e) => props.setAuthAddExpirationTime(e.target.checked)}
                                                            // disabled={props.isTemplateApproved}
                                                        >
                                                            Add expiration time for the code
                                                        </Checkbox>
                                                        {props.authAddExpirationTime && (
                                                            <FormControl>
                                                                <FormLabel fontSize="sm">Code Expiration Time (minutes)</FormLabel>
                                                                <NumberInput
                                                                    value={props.authCodeExpiryMinutes ?? ''}
                                                                    min={1}
                                                                    max={90}
                                                                    step={1}
                                                                    clampValueOnBlur
                                                                    keepWithinRange
                                                                    // disabled={props.isTemplateApproved}
                                                                    onChange={(valueString, valueNumber) => {
                                                                        if (valueString === '') {
                                                                            props.setAuthCodeExpiryMinutes('');
                                                                            return;
                                                                        }
                                                                        if (!isNaN(valueNumber)) {
                                                                            props.setAuthCodeExpiryMinutes(valueNumber);
                                                                        }
                                                                    }}
                                                                    size="sm"
                                                                >
                                                                    <NumberInputField placeholder="Enter minutes (1–90)" />
                                                                    <NumberInputStepper>
                                                                        <NumberIncrementStepper />
                                                                        <NumberDecrementStepper />
                                                                    </NumberInputStepper>
                                                                </NumberInput>
                                                                <Text fontSize="xs" color="gray.600" mt={1}>
                                                                    Set how long the authentication code remains valid (1-90 minutes)
                                                                </Text>
                                                            </FormControl>
                                                        )}
                                                    </VStack>
                                                </Box>
                                            )}

                                            {/* Message Expiry */}
                                            <Box>
                                                <Flex justify="space-between" align="center" mb={2}>
                                                    <FormLabel mb={0}>Message Expiry Time</FormLabel>
                                                    <Switch
                                                        isChecked={props.whatsappMessageExpiryEnabled}
                                                        onChange={(e) => props.setWhatsappMessageExpiryEnabled(e.target.checked)}
                                                        // disabled={props.isTemplateApproved}
                                                    />
                                                </Flex>
                                                <Text fontSize="xs" color="gray.600" mb={2}>
                                                    Set a custom validity period for message delivery.
                                                </Text>
                                                {props.whatsappMessageExpiryEnabled && (
                                                    <FormControl style={{ width: '200px' }}>
                                                        <Select
                                                            value={props.whatsappMessageExpiry}
                                                            onChange={(e) => props.setWhatsappMessageExpiry(e.target.value)}
                                                            // disabled={props.isTemplateApproved}
                                                        >
                                                            {expiryOptions.map((option) => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            </Box>
                                        </VStack>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Right Panel - Preview */}
                            <Box>
                                <Box position="sticky" top={4}>
                                    <Text fontWeight="medium" fontSize="sm" color="gray.600" mb={3}>
                                        Preview
                                    </Text>
                                    <WhatsAppPreview
                                        header={props.whatsappHeader}
                                        body={props.whatsappBody}
                                        footer={props.whatsappFooter}
                                        buttons={props.whatsappButtons}
                                        otpType={props.otpType}
                                        authAddExpirationTime={props.authAddExpirationTime}
                                        authCodeExpiryMinutes={props.authCodeExpiryMinutes}
                                        templateType={props.templateType}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </>
                )}
            </VStack>
        ),
        'SMS': () => (
            <VStack spacing={4} align="stretch">
                <Flex p={4} bg={bgMuted} borderRadius="lg" justify="space-between" align="center">
                    <HStack spacing={3}>
                        <LuMessageSquare size={20} color="blue" />
                        <Box>
                            <Text fontWeight="medium">SMS Channel</Text>
                            <Text fontSize="sm" color="gray.600">Simple text messages</Text>
                        </Box>
                    </HStack>

                    <HStack spacing={2}>
                        <Switch
                            id="enable-SMS"
                            isChecked={props.selectedChannels.includes('SMS')}
                            onChange={() => props.handleChannelToggle('SMS')}
                            colorScheme="green"
                        />
                        <FormLabel
                            htmlFor="enable-SMS"
                            mb={0}
                            cursor="pointer"
                            fontWeight="medium"
                        >
                            {props.selectedChannels.includes('SMS') ? 'Enabled' : 'Disabled'}
                        </FormLabel>
                    </HStack>
                </Flex>

                {props.selectedChannels.includes('SMS') && (
                    <Grid templateColumns={{ base: '1fr', lg: '3fr 1fr' }} gap={6}>
                        <Box className='box-style-card'>
                            <Box>
                                <FormControl isRequired>
                                    <FormLabel>SMS Message</FormLabel>
                                    <Textarea
                                        value={props.smsBody}
                                        onChange={(e) => props.setSmsBody(e.target.value)}
                                        placeholder="Enter SMS message content"
                                        rows={4}
                                    />

                                    <Flex justify="space-between" align="center" mt={2}>
                                        <HStack spacing={2}>
                                            <Text fontSize="xs" color={props.smsBody.length > 160 ? 'orange.600' : 'gray.600'}>
                                                {props.smsBody.length} characters
                                            </Text>

                                            {props.smsBody.length > 160 && (
                                                <>
                                                    <Text color="gray.400">•</Text>
                                                    <Tag colorScheme="orange">
                                                        <HStack spacing={1}>
                                                            <LuHash size={12} />
                                                            <Text fontSize="xs">{Math.ceil(props.smsBody.length / 160)} SMS Messages</Text>
                                                        </HStack>
                                                    </Tag>
                                                </>
                                            )}
                                        </HStack>
                                    </Flex>

                                    {props.smsBody.length > 160 && (
                                        <Alert status="info" mt={2}>
                                            <AlertIcon />
                                            <AlertDescription fontSize="sm">
                                                <Text fontWeight="medium">Multi-part SMS detected</Text>
                                                <Text>
                                                    This message will be sent as {Math.ceil(props.smsBody.length / 160)} separate SMS messages.
                                                </Text>
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    {props.smsBody.length <= 160 && props.smsBody.length > 0 && (
                                        <HStack spacing={1} mt={2}>
                                            <LuTarget size={12} color="green" />
                                            <Text fontSize="xs" color="green.600">
                                                Single SMS - fits within 160 character limit
                                            </Text>
                                        </HStack>
                                    )}
                                </FormControl>
                            </Box>
                        </Box>

                        <Box>
                            <Box position="sticky" top={4}>
                                <Text fontWeight="medium" fontSize="sm" color="gray.600" mb={3}>Preview</Text>
                                <SMSPreview body={props.smsBody} />
                            </Box>
                        </Box>
                    </Grid>
                )}
            </VStack>
        ),
        'Push Notification': () => (
            <VStack spacing={4} align="stretch">
                <Flex p={4} bg={bgMuted} borderRadius="lg" justify="space-between" align="center">
                    <HStack spacing={3}>
                        <LuBell size={20} color="orange" />
                        <Box>
                            <Text fontWeight="medium">Push Notification Channel</Text>
                            <Text fontSize="sm" color="gray.600">Mobile app notifications</Text>
                        </Box>
                    </HStack>

                    <HStack spacing={2}>
                        <Switch
                            id="enable-push"
                            isChecked={props.selectedChannels.includes('Push Notification')}
                            onChange={() => props.handleChannelToggle('Push Notification')}
                            colorScheme="green"
                        />
                        <FormLabel
                            htmlFor="enable-push"
                            mb={0}
                            cursor="pointer"
                            fontWeight="medium"
                        >
                            {props.selectedChannels.includes('Push Notification') ? 'Enabled' : 'Disabled'}
                        </FormLabel>
                    </HStack>
                </Flex>

                {props.selectedChannels.includes('Push Notification') && (
                    <Grid templateColumns={{ base: '1fr', lg: '3fr 1fr' }} gap={6}>
                        <Box className='box-style-card'>
                            <Box>
                                <VStack spacing={4} align="stretch">
                                    <FormControl isRequired>
                                        <FormLabel>Notification Title</FormLabel>
                                        <Input
                                            value={props.pushTitle}
                                            onChange={(e) => props.setPushTitle(e.target.value)}
                                            placeholder="Enter notification title"
                                            maxLength={50}
                                        />
                                        <Flex justify="flex-end" mt={1}>
                                            {props.getCharacterCount(props.pushTitle, 50)}
                                        </Flex>
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel>Notification Body</FormLabel>
                                        <Textarea
                                            value={props.pushBody}
                                            onChange={(e) => props.setPushBody(e.target.value)}
                                            placeholder="Enter notification body"
                                            rows={3}
                                            maxLength={150}
                                        />
                                        <Flex justify="flex-end" mt={1}>
                                            {props.getCharacterCount(props.pushBody, 150)}
                                        </Flex>
                                    </FormControl>
                                    {props.templateType !== "1" && (
                                        <FormControl>
                                            <FormLabel>Deep Link (Optional)</FormLabel>
                                            <Input
                                                value={props.deeplink || ''}
                                                onChange={(e) => props.setPushDeepLink(e.target.value)}
                                                placeholder="e.g., myapp://screen/details"
                                            />
                                            <Text fontSize="xs" color="gray.600" mt={1}>
                                                Deep link to navigate users to a specific screen in your app
                                            </Text>
                                        </FormControl>
                                    )}

                                    <ImageUploader
                                        label="Push Notification Image (Optional)"
                                        accept="image/*"
                                        onChange={(e) => props.handleFileUpload(e, 'push-image')}
                                        imageUrl={props.pushImage}
                                        onRemove={() => props.removeImage('push-image')}
                                        uploading={props.uploading}
                                        targetId="push-image-file"
                                        hint="Add an eye-catching image to your push notification"
                                    />
                                </VStack>
                            </Box>
                        </Box>

                        <Box>
                            <Box position="sticky" top={4}>
                                <Text fontWeight="medium" fontSize="sm" color="gray.600" mb={3}>Preview</Text>
                                <PushPreview title={props.pushTitle} body={props.pushBody} imageUrl={props.pushImage} />
                            </Box>
                        </Box>
                    </Grid>
                )}
            </VStack>
        ),
        'In-App Notification': () => (
            <VStack spacing={4} align="stretch">
                <Flex p={4} bg={bgMuted} borderRadius="lg" justify="space-between" align="center">
                    <HStack spacing={3}>
                        <LuSmartphone size={20} color="cyan" />
                        <Box>
                            <Text fontWeight="medium">In-App Notification Channel</Text>
                            <Text fontSize="sm" color="gray.600">In-app messages and alerts</Text>
                        </Box>
                    </HStack>

                    <HStack spacing={2}>
                        <Switch
                            id="enable-in-app"
                            isChecked={props.selectedChannels.includes('In-App Notification')}
                            onChange={() => props.handleChannelToggle('In-App Notification')}
                            colorScheme="green"
                        />
                        <FormLabel
                            htmlFor="enable-in-app"
                            mb={0}
                            cursor="pointer"
                            fontWeight="medium"
                        >
                            {props.selectedChannels.includes('In-App Notification') ? 'Enabled' : 'Disabled'}
                        </FormLabel>
                    </HStack>
                </Flex>

                {props.selectedChannels.includes('In-App Notification') && (
                    <>
                        {props.selectedChannels.includes('Whatsapp') && (
                            <Flex justify="flex-end">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    leftIcon={<LuArrowLeft />}
                                    onClick={props.syncInAppToWhatsApp}
                                >
                                    Sync to WhatsApp
                                </Button>
                            </Flex>
                        )}

                        <Grid templateColumns={{ base: '1fr', lg: '3fr 1fr' }} gap={6}>
                            <Box>
                                <Box className='box-style-card'>
                                    <Box>
                                        <VStack spacing={1} align="stretch">
                                            <VStack spacing={4} align="stretch">
                                                {props.templateType !== "1" && (
                                                    <>

                                                        <Text fontWeight="medium" mb={1}>Header (Optional)</Text>
                                                        <FormControl>
                                                            <FormLabel>Header Type</FormLabel>
                                                            <Select
                                                                value={props.inAppHeader.type || 'none'}
                                                                onChange={(e) => props.handleInAppHeaderTypeChange(e.target.value === 'none' ? "none" : e.target.value)}
                                                            >
                                                                <option value="none">None</option>
                                                                <option value="text">Text</option>
                                                                <option value="image">Image</option>
                                                                <option value="video">Video</option>
                                                                <option value="document">Document</option>
                                                            </Select>
                                                        </FormControl>
                                                    </>
                                                )}

                                                {props.inAppHeader.type === 'text' && (
                                                    props.templateType !== "1" && (
                                                        <FormControl isRequired>
                                                            <FormLabel>Header Text</FormLabel>
                                                            <Input
                                                                value={props.inAppHeader.content}
                                                                onChange={(e) => props.setInAppHeader(prev => ({ ...prev, content: e.target.value }))}
                                                                placeholder="Enter header text"
                                                                maxLength={60}
                                                            />
                                                            <Flex justify="flex-end" mt={1}>
                                                                {props.getCharacterCount(props.inAppHeader.content, 60)}
                                                            </Flex>
                                                            {containsVariables(props.inAppHeader.content) && (
                                                                <Alert status="error" mt={2}>
                                                                    <AlertIcon />
                                                                    <AlertDescription fontSize="sm">
                                                                        Variables are not allowed in header
                                                                    </AlertDescription>
                                                                </Alert>
                                                            )}
                                                        </FormControl>
                                                    )
                                                )}

                                                {(props.inAppHeader.type === 'image' ||
                                                    props.inAppHeader.type === 'video' ||
                                                    props.inAppHeader.type === 'document') && (
                                                        <ImageUploader
                                                            label={`${props.inAppHeader.type.charAt(0).toUpperCase() + props.inAppHeader.type.slice(1)} Header`}
                                                            accept={
                                                                props.inAppHeader.type === 'image' ? 'image/*' :
                                                                    props.inAppHeader.type === 'video' ? 'video/*' :
                                                                        'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                                                            }
                                                            onChange={(e) => props.handleFileUpload(e, 'inapp-header')}
                                                            imageUrl={props.inAppHeader.file}
                                                            onRemove={() => props.removeImage('inapp-header')}
                                                            uploading={props.uploading}
                                                            targetId="inapp-header-file"
                                                            hint="This will appear at the top of your in-app notification"
                                                        />
                                                    )}
                                            </VStack>

                                            <FormControl>
                                                <FormLabel>Body</FormLabel>
                                                <Textarea
                                                    value={props.inAppBody}
                                                    onChange={(e) => props.setInAppBody(e.target.value)}
                                                    placeholder="Enter message body"
                                                    rows={4}
                                                    maxLength={1024}
                                                />
                                                <Flex justify="flex-end" mt={1}>
                                                    {props.getCharacterCount(props.inAppBody, 1024)}
                                                </Flex>
                                                {containsEmojis(props.inAppBody) && (
                                                    <Alert status="error" mt={2}>
                                                        <AlertIcon />
                                                        <AlertDescription fontSize="sm">
                                                            Emojis are not allowed in message body
                                                        </AlertDescription>
                                                    </Alert>
                                                )}
                                            </FormControl>

                                            {props.templateType !== "1" && (
                                                <FormControl>
                                                    <FormLabel>Footer (Optional)</FormLabel>
                                                    <Input
                                                        value={props.inAppFooter}
                                                        onChange={(e) => props.setInAppFooter(e.target.value)}
                                                        placeholder="Enter footer text"
                                                        maxLength={60}
                                                    />
                                                    <Flex justify="flex-end" mt={1}>
                                                        {props.getCharacterCount(props.inAppFooter, 60)}
                                                    </Flex>
                                                    {containsVariables(props.inAppFooter) && (
                                                        <Alert status="error" mt={2}>
                                                            <AlertIcon />
                                                            <AlertDescription fontSize="sm">
                                                                Variables are not allowed in footer
                                                            </AlertDescription>
                                                        </Alert>
                                                    )}
                                                </FormControl>
                                            )}

                                            {props.templateType !== "1" && (
                                                <Box>
                                                    <Flex justify="space-between" align="center" mb={3}>
                                                        <Text fontWeight="medium">Call-to-Action Buttons (Optional)</Text>
                                                        {props.inAppButtons.length < 3 && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                leftIcon={<LuPlus />}
                                                                onClick={props.addInAppButton}
                                                            >
                                                                Add Button
                                                            </Button>
                                                        )}
                                                    </Flex>
                                                    {props.renderButtonConfiguration(
                                                        props.inAppButtons,
                                                        props.updateInAppButton,
                                                        props.removeInAppButton,
                                                        3,
                                                        'Button'
                                                    )}
                                                </Box>
                                            )}
                                        </VStack>
                                    </Box>
                                </Box>
                            </Box>

                            <Box>
                                <Box position="sticky" top={4}>
                                    <Text fontWeight="medium" fontSize="sm" color="gray.600" mb={3}>Preview</Text>
                                    <InAppPreview
                                        header={props.inAppHeader}
                                        body={props.inAppBody}
                                        footer={props.inAppFooter}
                                        buttons={props.inAppButtons}
                                        otpType={props.otpType}
                                        isWhatsapp={props.selectedChannels.includes('Whatsapp') ? true : false}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </>
                )}
            </VStack>
        ),
        'Voice (IVR)': () => (
            <VStack spacing={4} align="stretch">
                <Flex p={4} bg={bgMuted} borderRadius="lg" justify="space-between" align="center">
                    <HStack spacing={3}>
                        <LuPhone size={20} color="purple" />
                        <Box>
                            <Text fontWeight="medium">Voice Call Channel</Text>
                            <Text fontSize="sm" color="gray.600">Automated voice calls</Text>
                        </Box>
                    </HStack>

                    <HStack spacing={2}>
                        <Switch
                            id="enable-voice"
                            isChecked={props.selectedChannels.includes('Voice (IVR)')}
                            onChange={() => props.handleChannelToggle('Voice (IVR)')}
                            colorScheme="green"
                        />
                        <FormLabel
                            htmlFor="enable-voice"
                            mb={0}
                            cursor="pointer"
                            fontWeight="medium"
                        >
                            {props.selectedChannels.includes('Voice (IVR)') ? 'Enabled' : 'Disabled'}
                        </FormLabel>
                    </HStack>
                </Flex>

                {props.selectedChannels.includes('Voice (IVR)') && (
                    <Grid templateColumns={{ base: '1fr', lg: '3fr 1fr' }} gap={6}>
                        <Box className='box-style-card'>
                            <VStack spacing={5} align="stretch">

                                {/* ── Voice Call Type ── */}
                                <Box>
                                    <FormLabel fontWeight="semibold" mb={3}>Voice Call Type</FormLabel>
                                    <RadioGroup
                                        value={props.voiceCallType || 'text_to_voice'}
                                        onChange={props.setVoiceCallType}
                                    >
                                        <VStack spacing={3} align="stretch">
                                            <Box
                                                as="label"
                                                htmlFor="voice-type-text"
                                                p={3}
                                                border="1px solid"
                                                borderColor={(props.voiceCallType || 'text_to_voice') === 'text_to_voice' ? 'purple.300' : 'gray.200'}
                                                borderRadius="lg"
                                                cursor="pointer"
                                                bg={(props.voiceCallType || 'text_to_voice') === 'text_to_voice' ? 'purple.50' : 'white'}
                                                _hover={{ bg: 'purple.50' }}
                                                transition="all 0.15s"
                                            >
                                                <HStack spacing={3}>
                                                    <Radio id="voice-type-text" value="text_to_voice" colorScheme="purple" />
                                                    <HStack spacing={2}>
                                                        <LuPhone size={16} color="#9810fa" />
                                                        <Box>
                                                            <Text fontWeight="medium" fontSize="sm">Text to Voice</Text>
                                                            <Text fontSize="xs" color="gray.500">Convert text script to automated voice call</Text>
                                                        </Box>
                                                    </HStack>
                                                </HStack>
                                            </Box>

                                            <Box
                                                as="label"
                                                htmlFor="voice-type-recorded"
                                                p={3}
                                                border="1px solid"
                                                borderColor={props.voiceCallType === 'recorded_call' ? 'purple.300' : 'gray.200'}
                                                borderRadius="lg"
                                                cursor="pointer"
                                                bg={props.voiceCallType === 'recorded_call' ? 'purple.50' : 'white'}
                                                _hover={{ bg: 'purple.50' }}
                                                transition="all 0.15s"
                                            >
                                                <HStack spacing={3}>
                                                    <Radio id="voice-type-recorded" value="recorded_call" colorScheme="purple" />
                                                    <HStack spacing={2}>
                                                        <LuPhone size={16} color="#9810fa" />
                                                        <Box>
                                                            <Text fontWeight="medium" fontSize="sm">Recorded Call</Text>
                                                            <Text fontSize="xs" color="gray.500">Use pre-recorded audio file for voice calls</Text>
                                                        </Box>
                                                    </HStack>
                                                </HStack>
                                            </Box>
                                        </VStack>
                                    </RadioGroup>
                                </Box>

                                <Divider />

                                {/* ── Text to Voice ── */}
                                {(props.voiceCallType || 'text_to_voice') === 'text_to_voice' && (
                                    <>
                                        <FormControl isRequired>
                                            <FormLabel>Voice Script</FormLabel>
                                            <Textarea
                                                value={props.voiceScript}
                                                onChange={(e) => props.setVoiceScript(e.target.value)}
                                                placeholder="Enter voice call script or generate from master content"
                                                rows={4}
                                                maxLength={300}
                                            />
                                            <Flex justify="space-between" align="center" mt={1}>
                                                <Text fontSize="xs" color={props.voiceScript.length > 300 ? 'red.500' : 'gray.500'}>
                                                    {props.voiceScript.length}/300 characters
                                                </Text>
                                            </Flex>
                                            {props.voiceScript.length > 300 && (
                                                <Alert status="warning" mt={2} borderRadius="md">
                                                    <AlertIcon />
                                                    <AlertDescription fontSize="sm">
                                                        Voice script exceeds recommended 300 character limit. Longer scripts may be truncated or cause delivery issues.
                                                    </AlertDescription>
                                                </Alert>
                                            )}
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel>Retry Attempts</FormLabel>
                                            <Select
                                                value={props.voiceRetries || ''}
                                                onChange={(e) => props.setVoiceRetries(e.target.value)}
                                                placeholder="Select retry attempts"
                                            >
                                                <option value="1">1 attempt</option>
                                                <option value="2">2 attempts</option>
                                                <option value="3">3 attempts</option>
                                                <option value="5">5 attempts</option>
                                            </Select>
                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                                Number of retry attempts if the call fails or goes unanswered
                                            </Text>
                                        </FormControl>
                                    </>
                                )}

                                {/* ── Recorded Call ── */}
                                {props.voiceCallType === 'recorded_call' && (
                                    <>
                                        <FormControl isRequired>
                                            <FormLabel>Recorded Call ID</FormLabel>
                                            <Input
                                                value={props.voiceCallId || ''}
                                                onChange={(e) => props.setVoiceCallId(e.target.value)}
                                                placeholder="Enter the Call ID of your recorded audio file"
                                            />
                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                                This is the unique identifier of your pre-recorded audio file stored in the system
                                            </Text>
                                        </FormControl>

                                        <Divider />

                                        {/* Variable Source */}
                                        <Box>
                                            <FormLabel fontWeight="semibold" mb={3}>Required Variables</FormLabel>
                                            <RadioGroup
                                                value={props.voiceVariableSource || 'from_channels'}
                                                onChange={props.setVoiceVariableSource}
                                            >
                                                <VStack spacing={3} align="stretch" mb={4}>
                                                    <Box
                                                        as="label"
                                                        htmlFor="var-source-channels"
                                                        p={3}
                                                        border="1px solid"
                                                        borderColor={(props.voiceVariableSource || 'from_channels') === 'from_channels' ? 'purple.300' : 'gray.200'}
                                                        borderRadius="lg"
                                                        cursor="pointer"
                                                        bg={(props.voiceVariableSource || 'from_channels') === 'from_channels' ? 'purple.50' : 'white'}
                                                        _hover={{ bg: 'gray.50' }}
                                                        transition="all 0.15s"
                                                    >
                                                        <HStack spacing={3}>
                                                            <Radio id="var-source-channels" value="from_channels" colorScheme="purple" />
                                                            <HStack spacing={2}>
                                                                <LuHash size={16} color="#9810fa" />
                                                                <Box>
                                                                    <Text fontWeight="medium" fontSize="sm">Use Variables from Other Channels</Text>
                                                                    <Text fontSize="xs" color="gray.500">Use variables already defined in WhatsApp, SMS, or other channels</Text>
                                                                </Box>
                                                            </HStack>
                                                        </HStack>
                                                    </Box>

                                                    <Box
                                                        as="label"
                                                        htmlFor="var-source-custom"
                                                        p={3}
                                                        border="1px solid"
                                                        borderColor={props.voiceVariableSource === 'custom' ? 'purple.300' : 'gray.200'}
                                                        borderRadius="lg"
                                                        cursor="pointer"
                                                        bg={props.voiceVariableSource === 'custom' ? 'purple.50' : 'white'}
                                                        _hover={{ bg: 'gray.50' }}
                                                        transition="all 0.15s"
                                                    >
                                                        <HStack spacing={3}>
                                                            <Radio id="var-source-custom" value="custom" colorScheme="purple" />
                                                            <HStack spacing={2}>
                                                                <LuPlus size={16} color="#9810fa" />
                                                                <Box>
                                                                    <Text fontWeight="medium" fontSize="sm">Define Custom Variables</Text>
                                                                    <Text fontSize="xs" color="gray.500">Add specific variables for this voice call only</Text>
                                                                </Box>
                                                            </HStack>
                                                        </HStack>
                                                    </Box>
                                                </VStack>
                                            </RadioGroup>

                                            {/* From Channels — show existing channel variables */}
                                            {(props.voiceVariableSource || 'from_channels') === 'from_channels' && (
                                                (props.allChannelVariables || []).length === 0 ? (
                                                    <Flex
                                                        direction="column"
                                                        align="center"
                                                        justify="center"
                                                        py={8}
                                                        border="1px solid"
                                                        borderColor="gray.200"
                                                        borderRadius="lg"
                                                        bg="gray.50"
                                                        color="gray.400"
                                                    >
                                                        <LuHash size={32} style={{ marginBottom: '8px', opacity: 0.3 }} />
                                                        <Text fontSize="sm">No variables found in other channels</Text>
                                                        <Text fontSize="xs" mt={1}>Add variables to WhatsApp, SMS, or other channel content first</Text>
                                                    </Flex>
                                                ) : (
                                                    <VStack spacing={3} align="stretch">
                                                        <Alert status="info" borderRadius="md" bg="blue.50" border="1px solid" borderColor="blue.200">
                                                            <AlertIcon color="blue.500" />
                                                            <AlertDescription fontSize="xs" color="blue.800">
                                                                The following variables are detected from other channels and will be available for this voice call.
                                                            </AlertDescription>
                                                        </Alert>
                                                        <Box p={4} border="1px solid" borderColor="purple.200" borderRadius="lg" bg="purple.50">
                                                            <Flex flexWrap="wrap" gap={2}>
                                                                {(props.allChannelVariables || []).map((variable, index) => (
                                                                    <Box
                                                                        key={index}
                                                                        px={3}
                                                                        py={1.5}
                                                                        bg="white"
                                                                        border="1px solid"
                                                                        borderColor="purple.300"
                                                                        borderRadius="md"
                                                                        fontSize="sm"
                                                                        fontFamily="mono"
                                                                    >
                                                                        ${variable}
                                                                    </Box>
                                                                ))}
                                                            </Flex>
                                                        </Box>
                                                    </VStack>
                                                )
                                            )}

                                            {/* Custom Variables */}
                                            {props.voiceVariableSource === 'custom' && (
                                                <VStack spacing={3} align="stretch">
                                                    <Flex justify="space-between" align="center">
                                                        <Text fontSize="sm" color="gray.500">Add custom variables for this voice call</Text>
                                                        <Button
                                                            leftIcon={<LuPlus />}
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => props.setIvrVariables([...(props.ivrVariables || []), ''])}
                                                        >
                                                            Add Variable
                                                        </Button>
                                                    </Flex>

                                                    {(props.ivrVariables || []).length === 0 ? (
                                                        <Flex
                                                            direction="column"
                                                            align="center"
                                                            justify="center"
                                                            py={8}
                                                            border="1px solid"
                                                            borderColor="gray.200"
                                                            borderRadius="lg"
                                                            bg="gray.50"
                                                            color="gray.400"
                                                        >
                                                            <LuHash size={32} style={{ marginBottom: '8px', opacity: 0.3 }} />
                                                            <Text fontSize="sm">No variables defined yet</Text>
                                                            <Text fontSize="xs" mt={1}>Add variables that will be used in your recorded call</Text>
                                                        </Flex>
                                                    ) : (
                                                        <VStack spacing={3} align="stretch">
                                                            {(props.ivrVariables || []).map((varName, idx) => (
                                                                <Box
                                                                    key={idx}
                                                                    p={4}
                                                                    border="1px solid"
                                                                    borderColor="purple.200"
                                                                    borderRadius="lg"
                                                                    bg="purple.50"
                                                                >
                                                                    <HStack align="flex-end" spacing={2}>
                                                                        <Box flex={1}>
                                                                            <FormLabel fontSize="xs" mb={1}>Variable Name</FormLabel>
                                                                            <Input
                                                                                value={varName}
                                                                                onChange={(e) => {
                                                                                    const updated = [...props.ivrVariables];
                                                                                    updated[idx] = e.target.value;
                                                                                    props.setIvrVariables(updated);
                                                                                }}
                                                                                placeholder="e.g., customer_name, order_id"
                                                                                bg="white"
                                                                                fontFamily="mono"
                                                                            />
                                                                        </Box>
                                                                        <IconButton
                                                                            icon={<LuTrash2 />}
                                                                            variant="ghost"
                                                                            colorScheme="red"
                                                                            onClick={() =>
                                                                                props.setIvrVariables(
                                                                                    props.ivrVariables.filter((_, i) => i !== idx)
                                                                                )
                                                                            }
                                                                            aria-label="Remove variable"
                                                                        />
                                                                    </HStack>
                                                                </Box>
                                                            ))}
                                                        </VStack>
                                                    )}

                                                    {(props.ivrVariables || []).some((v) => v.trim()) && (
                                                        <Alert status="info" borderRadius="md" bg="blue.50" border="1px solid" borderColor="blue.200">
                                                            <AlertIcon color="blue.500" />
                                                            <AlertDescription fontSize="xs" color="blue.800">
                                                                These variables can be dynamically replaced when the call is made. Make sure they match the placeholders in your recorded audio configuration.
                                                            </AlertDescription>
                                                        </Alert>
                                                    )}
                                                </VStack>
                                            )}
                                        </Box>

                                        <FormControl>
                                            <FormLabel>Retry Attempts</FormLabel>
                                            <Select
                                                value={props.voiceRetries || ''}
                                                onChange={(e) => props.setVoiceRetries(e.target.value)}
                                                placeholder="Select retry attempts"
                                            >
                                                <option value="1">1 attempt</option>
                                                <option value="2">2 attempts</option>
                                                <option value="3">3 attempts</option>
                                                <option value="5">5 attempts</option>
                                            </Select>
                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                                Number of retry attempts if the call fails or goes unanswered
                                            </Text>
                                        </FormControl>
                                    </>
                                )}
                            </VStack>
                        </Box>

                        <Box>
                            <Box position="sticky" top={4}>
                                <Text fontWeight="medium" fontSize="sm" color="gray.600" mb={3}>Preview</Text>
                                <VoicePreview
                                    script={
                                        props.voiceCallType === 'recorded_call'
                                            ? `[Recorded Call]\nCall ID: ${props.voiceCallId || '—'}`
                                            : props.voiceScript
                                    }
                                />
                            </Box>
                        </Box>
                    </Grid>
                )}
            </VStack>
        ),
        'E-Mail': () => (
            <VStack spacing={4} align="stretch">
                <Flex p={4} bg={bgMuted} borderRadius="lg" justify="space-between" align="center">
                    <HStack spacing={3}>
                        <LuMail size={20} color="red" />
                        <Box>
                            <Text fontWeight="medium">Email Channel</Text>
                            <Text fontSize="sm" color="gray.600">HTML emails with rich content</Text>
                        </Box>
                    </HStack>

                    <HStack spacing={2}>
                        <Switch
                            id="enable-mail"
                            isChecked={props.selectedChannels.includes('E-Mail')}
                            onChange={() => props.handleChannelToggle('E-Mail')}
                            colorScheme="green"
                        />
                        <FormLabel
                            htmlFor="enable-mail"
                            mb={0}
                            cursor="pointer"
                            fontWeight="medium"
                        >
                            {props.selectedChannels.includes('E-Mail') ? 'Enabled' : 'Disabled'}
                        </FormLabel>
                    </HStack>
                </Flex>

                {props.selectedChannels.includes('E-Mail') && (
                    <Grid templateColumns={{ base: '1fr', lg: '3fr 1fr' }} gap={6}>
                        <Box className='box-style-card'>
                            <Box>
                                <VStack spacing={4} align="stretch">
                                    <FormControl isRequired>
                                        <FormLabel>Email Subject</FormLabel>
                                        <Input
                                            value={props.emailSubject}
                                            onChange={(e) => props.setEmailSubject(e.target.value)}
                                            placeholder="Enter email subject"
                                            maxLength={100}
                                        />
                                        <Flex justify="flex-end" mt={1}>
                                            {props.getCharacterCount(props.emailSubject, 100)}
                                        </Flex>
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel>Content Type</FormLabel>
                                        <RadioGroup
                                            value={props.emailType}
                                            onChange={props.setEmailType}
                                        >
                                            <Stack direction="column" spacing={2}>
                                                <Radio value="text">Plain Text</Radio>
                                                <Radio value="html">HTML Content</Radio>
                                            </Stack>
                                        </RadioGroup>
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel>
                                            {props.emailType === 'html' ? 'HTML Content' : 'Plain Text Content'}
                                        </FormLabel>
                                        <Textarea
                                            value={props.emailHtml}
                                            onChange={(e) => props.setEmailHtml(e.target.value)}
                                            placeholder={`Enter ${props.emailType === 'html' ? 'HTML' : 'plain text'} content`}
                                            rows={8}
                                            fontFamily={props.emailType === 'html' ? 'mono' : 'inherit'}
                                        />
                                    </FormControl>
                                </VStack>
                            </Box>
                        </Box>

                        <Box>
                            <Box position="sticky" top={4}>
                                <Text fontWeight="medium" fontSize="sm" color="gray.600" mb={3}>Preview</Text>
                                <EmailPreview
                                    subject={props.emailSubject}
                                    body={props.emailHtml}
                                    emailType={props.emailType}
                                />
                            </Box>
                        </Box>
                    </Grid>
                )}
            </VStack>
        ),
    };

    return (
        <Box className='box-style-card' bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <Box>
                <Tabs variant="enclosed" index={props.tabIndex} onChange={props.setTabIndex}>
                    <TabList bg="#f1f5f9" p="1" borderRadius="full" display="flex" justifyContent="space-between">
                        {props.availableChannels.map((channel) => {
                            const channelName = channel.name === "Push Notification" ? "Push Notification" :
                                channel.name === "In-App Notification" ? "In-App Notification" :
                                    channel.name === "Voice (IVR)" ? "Voice" :
                                        channel.name === "E-Mail" ? "Email" : channel.name;

                            return (
                                <Tab
                                    key={channel.name}
                                    position="relative"
                                    flex="1"
                                    _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }}
                                    borderRadius="full"
                                    fontSize="sm"
                                >
                                    <HStack spacing={2}>
                                        <Icon as={channel.icon} />
                                        <Text display={{ base: 'none', sm: 'inline' }}>{channelName}</Text>
                                    </HStack>

                                    {props.selectedChannels.includes(channel.name) && (
                                        <Box
                                            position="absolute"
                                            top={2}
                                            right={3}
                                            w={4}
                                            h={4}
                                            bg={channel.color}
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <LuCheckCircle size={10} color="white" />
                                        </Box>
                                    )}
                                </Tab>
                            );
                        })}
                    </TabList>
                        {console.log(props.availableChannels,"props.availableChannels")}
                    <TabPanels>
                        {props.availableChannels.map((channel) => (
                            <TabPanel key={channel.name}>
                                {channelPanels[channel.name]?.()}
                            </TabPanel>
                        ))}
                    </TabPanels>
                </Tabs>
            </Box>
        </Box>
    );
}